import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { DEFAULT_ADDRESS_TYPE } from '../../../constants/common'
import {
  createPayload,
  getLatLongByPincodeRequest,
  updateAddressRequest,
} from '../../../redux/slices'

const formFields = [
  {
    name: 'address',
    label: 'Address Line 1',
    placeholder: 'Flat/House Number/Floor/Building',
    focus: true,
    validation: {
      required: 'Address Line 1 is required',
      minLength: {
        value: 5,
        message: 'Address should be at least 5 characters long',
      },
    },
  },
  {
    name: 'street',
    label: 'Address Line 2',
    placeholder: 'Street/Area/Locality',
    validation: {
      required: 'Address Line 2 is required',
      minLength: {
        value: 5,
        message: 'Address should be at least 5 characters long',
      },
    },
  },
  {
    name: 'pincode',
    label: 'Pincode',
    placeholder: 'Enter pincode',
    type: 'number',
    minLength: 6,
    maxLength: 6,
    validation: {
      required: 'Pincode is required',
      pattern: {
        value: /^[0-9]{6}$/,
        message: 'Please enter a valid 6-digit pincode',
      },
    },
  },
  {
    name: 'landmark',
    label: 'Landmark',
    placeholder: 'Enter landmark',
    validation: {
      minLength: {
        value: 3,
        message: 'Landmark should be at least 3 characters long',
      },
    },
  },
]

const useAddAddressDetails = ({ onProceed }) => {
  const {
    updateAddress: { loading, success, error, data },
  } = useSelector((state) => state.diagnostics)
  const { userDetails } = useSelector((state) => state.auth)
  const { formFields: storedFormFields } = useSelector(
    (state) => state.formPayload,
  )
  const pincodeState = useSelector((state) => state.pincode)

  const dispatch = useDispatch()

  const onSubmit = (payload) => {
    dispatch(updateAddressRequest(payload))
    dispatch(
      createPayload({
        addressPayload: payload,
      }),
    )
  }

  useEffect(() => {
    if (!loading && success && data) {
      dispatch(
        createPayload({
          addressResponse: data,
        }),
      )
      onProceed()
    }
  }, [loading, success, data])

  const defaultFormValues = useMemo(() => {
    const { address, street, pincode, latitude, longitude, landmark } =
      storedFormFields?.addressPayload || {}
    return {
      name: userDetails?.data?.name || '',
      address: address || '',
      street: street || '',
      pincode: pincode || null,
      latitude: latitude || '',
      longitude: longitude || '',
      contact_no: userDetails?.data?.contact_no || '',
      landmark: landmark || '',
      address_type: DEFAULT_ADDRESS_TYPE,
    }
  }, [userDetails, storedFormFields])

  const {
    register,
    handleSubmit,
    setValue,
    formState,
    watch,
    setFocus,
    setError,
  } = useForm({
    defaultValues: defaultFormValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const watchPincode = watch('pincode')

  useEffect(() => {
    if (
      watchPincode &&
      watchPincode.toString().length === 6 &&
      formState.isDirty
    ) {
      dispatch(getLatLongByPincodeRequest(Number(watchPincode)))
    }
  }, [watchPincode])

  useEffect(() => {
    const { data, loading, error, success } = pincodeState || {}
    if (formState.isDirty) {
      if (!loading && !error && success && data?.latitude && data?.longitude) {
        setValue('latitude', data?.latitude, { shouldValidate: true })
        setValue('longitude', data?.longitude, { shouldValidate: true })
        setFocus('landmark', { shouldSelect: false })
      } else {
        setError('pincode', { message: error || 'Pincode is Serviceable' })
      }
    }
  }, [pincodeState])

  return {
    error,
    loading,
    success,
    formFields,
    userDetails,
    onSubmit,
    register,
    setValue,
    handleSubmit,
    formState,
    pincodeState,
  }
}
export default useAddAddressDetails
